import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FaArrowUp, FaChevronDown, FaChevronUp, FaCommentDots, FaTimes, FaWrench } from 'react-icons/fa';
import { IconButton } from '../ui/Button';
import styles from './AgentChatShell.module.css';

export interface AgentToolOperationSummary {
  type: 'created' | 'updated' | 'deleted' | 'read' | 'error';
  entity: string;
  description: string;
}

export interface AgentChatAttachment {
  url: string;
  alt: string;
}

export interface AgentChatMessage {
  id: string;
  sender: 'user' | 'assistant';
  content: string;
  attachments?: AgentChatAttachment[];
  toolOperations?: AgentToolOperationSummary[];
}

interface ContextIndicator {
  title: string;
  percent: number;
  color: string;
}

interface AgentChatShellProps {
  inline?: boolean;
  assistantName: string;
  subtitle: string;
  contextIndicator?: ContextIndicator;
  headerAction?: React.ReactNode;
  error?: string | null;
  onDismissError: () => void;
  isLoading: boolean;
  messages: AgentChatMessage[];
  isAssistantTyping: boolean;
  typingLabel: string;
  emptyStateTitle: string;
  emptyStateText: string;
  draft: string;
  onDraftChange: (value: string) => void;
  placeholder: string;
  inputAriaLabel: string;
  canSend: boolean;
  onSend: () => void;
  sendAriaLabel: string;
  footerHint: string;
  launcherAriaLabel: string;
  closeAriaLabel: string;
  dialogAriaLabel: string;
  onOpen?: () => void;
  onClose?: () => void;
  composerLeadingAction?: React.ReactNode;
  pendingAttachments?: React.ReactNode;
}

const ToolCallSummary: React.FC<{ operations: AgentToolOperationSummary[] }> = ({ operations }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (operations.length === 0) return null;

  const errorCount = operations.filter((operation) => operation.type === 'error').length;

  return (
    <div className={styles.toolCallSummary}>
      <button
        className={styles.toolCallHeader}
        onClick={() => setIsExpanded(!isExpanded)}
        aria-expanded={isExpanded}
      >
        <span className={styles.toolCallIcon}>
          {FaWrench({ 'aria-hidden': 'true', size: 12 })}
        </span>
        <span className={styles.toolCallTitle}>
          {operations.length} action{operations.length !== 1 ? 's' : ''} taken
          {errorCount > 0 && <span className={styles.errorBadge}>{errorCount} failed</span>}
        </span>
        <span className={styles.toolCallChevron}>
          {isExpanded
            ? FaChevronUp({ 'aria-hidden': 'true', size: 10 })
            : FaChevronDown({ 'aria-hidden': 'true', size: 10 })}
        </span>
      </button>
      {isExpanded && (
        <ul className={styles.toolCallList}>
          {operations.map((operation, index) => (
            <li
              key={index}
              className={`${styles.toolCallItem} ${operation.type === 'error' ? styles.toolCallError : ''}`}
            >
              <span className={styles.toolCallType}>
                {operation.type === 'created' && '✓'}
                {operation.type === 'updated' && '↻'}
                {operation.type === 'deleted' && '✕'}
                {operation.type === 'read' && '↗'}
                {operation.type === 'error' && '⚠'}
              </span>
              <span className={styles.toolCallDescription}>{operation.description}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const formatContent = (content: string) => (
  content.split('\n').map((line, index) => {
    const trimmed = line.trim();
    if (trimmed.startsWith('- ') || trimmed.startsWith('• ')) {
      return <div key={index} className={styles.listItem}>{trimmed.slice(2)}</div>;
    }
    if (trimmed.startsWith('**') && trimmed.endsWith('**')) {
      return <div key={index} className={styles.boldLine}>{trimmed.slice(2, -2)}</div>;
    }
    if (trimmed === '') {
      return <div key={index} className={styles.spacer} />;
    }
    return <div key={index}>{line}</div>;
  })
);

export const AgentChatShell: React.FC<AgentChatShellProps> = ({
  inline = false,
  assistantName,
  subtitle,
  contextIndicator,
  headerAction,
  error,
  onDismissError,
  isLoading,
  messages,
  isAssistantTyping,
  typingLabel,
  emptyStateTitle,
  emptyStateText,
  draft,
  onDraftChange,
  placeholder,
  inputAriaLabel,
  canSend,
  onSend,
  sendAriaLabel,
  footerHint,
  launcherAriaLabel,
  closeAriaLabel,
  dialogAriaLabel,
  onOpen,
  onClose,
  composerLeadingAction,
  pendingAttachments,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const launcherRef = useRef<HTMLButtonElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const scrollAnchorRef = useRef<HTMLDivElement | null>(null);
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const inlineMountedRef = useRef(false);

  const close = useCallback(() => {
    onClose?.();
    setIsOpen(false);
    window.setTimeout(() => launcherRef.current?.focus(), 0);
  }, [onClose]);

  const open = useCallback(() => {
    onOpen?.();
    setIsOpen(true);
  }, [onOpen]);

  useEffect(() => {
    const element = inputRef.current;
    if (!element) return;
    element.style.height = 'auto';
    element.style.height = `${element.scrollHeight}px`;
  }, [draft, inline, isOpen]);

  useEffect(() => {
    if (inline) return;
    if (!isOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    window.setTimeout(() => {
      inputRef.current?.focus();
      scrollAnchorRef.current?.scrollIntoView?.({ block: 'end' });
    }, 0);

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };

    const updateViewportPosition = () => {
      if (overlayRef.current && window.visualViewport) {
        overlayRef.current.style.setProperty('--chat-viewport-height', `${window.visualViewport.height}px`);
        overlayRef.current.style.setProperty('--chat-viewport-top', `${window.visualViewport.offsetTop}px`);
      }
    };

    updateViewportPosition();
    document.addEventListener('keydown', onKeyDown);
    window.visualViewport?.addEventListener('resize', updateViewportPosition);
    window.visualViewport?.addEventListener('scroll', updateViewportPosition);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKeyDown);
      window.visualViewport?.removeEventListener('resize', updateViewportPosition);
      window.visualViewport?.removeEventListener('scroll', updateViewportPosition);
    };
  }, [close, inline, isOpen]);

  useEffect(() => {
    if (!inline && !isOpen) return;
    scrollAnchorRef.current?.scrollIntoView?.({ block: 'end' });
  }, [inline, isAssistantTyping, isOpen, messages.length]);

  useEffect(() => {
    if (!inline) {
      inlineMountedRef.current = false;
      return;
    }
    if (inlineMountedRef.current) return;
    inlineMountedRef.current = true;
    window.setTimeout(() => {
      inputRef.current?.focus();
      scrollAnchorRef.current?.scrollIntoView?.({ block: 'end' });
    }, 0);
  }, [inline]);

  const chatHeader = (
    <div className={styles.header}>
      <div className={styles.headerTitle}>
        <div className={styles.headerName}>{assistantName}</div>
        <div className={styles.headerSubtitle}>{subtitle}</div>
      </div>

      {contextIndicator && (
        <div className={styles.contextIndicator} title={contextIndicator.title}>
          <div
            className={styles.contextBar}
            style={{
              width: `${contextIndicator.percent}%`,
              backgroundColor: contextIndicator.color,
            }}
          />
        </div>
      )}

      {headerAction}

      {!inline && (
        <IconButton
          icon={FaTimes({ 'aria-hidden': 'true' })}
          aria-label={closeAriaLabel}
          variant="ghost"
          size="md"
          onClick={close}
        />
      )}
    </div>
  );

  const chatErrorBanner = error ? (
    <div className={styles.errorBanner}>
      <span aria-hidden="true">&#9888;&#65039;</span>
      <span>{error}</span>
      <button className={styles.errorDismiss} onClick={onDismissError} aria-label="Dismiss error">
        &times;
      </button>
    </div>
  ) : null;

  const chatMessages = (
    <div className={styles.messages}>
      {isLoading && messages.length === 0 && (
        <div className={styles.loadingState}>Loading conversation...</div>
      )}

      {!isLoading && messages.length === 0 && (
        <div className={styles.emptyState}>
          <div className={styles.emptyStateTitle}>{emptyStateTitle}</div>
          <div className={styles.emptyStateText}>{emptyStateText}</div>
        </div>
      )}

      {messages.map((message) => (
        <div
          key={message.id}
          className={`${styles.messageRow} ${message.sender === 'user' ? styles.rowUser : styles.rowAssistant}`}
        >
          <div className={`${styles.bubble} ${message.sender === 'user' ? styles.bubbleUser : styles.bubbleAssistant}`}>
            {message.attachments && message.attachments.length > 0 && (
              <div className={styles.messageImages}>
                {message.attachments.map((attachment, index) => (
                  <img
                    key={`${message.id}-attachment-${index}`}
                    src={attachment.url}
                    alt={attachment.alt}
                    className={styles.messageImage}
                    onClick={() => window.open(attachment.url, '_blank')}
                  />
                ))}
              </div>
            )}
            {message.sender === 'assistant' ? formatContent(message.content) : message.content}
            {message.sender === 'assistant' && message.toolOperations && message.toolOperations.length > 0 && (
              <ToolCallSummary operations={message.toolOperations} />
            )}
          </div>
        </div>
      ))}

      {isAssistantTyping && (
        <div className={`${styles.messageRow} ${styles.rowAssistant}`}>
          <div className={`${styles.bubble} ${styles.bubbleAssistant}`} aria-label={typingLabel}>
            <span className={styles.typingDots} aria-hidden="true">
              <span className={styles.typingDot} />
              <span className={styles.typingDot} />
              <span className={styles.typingDot} />
            </span>
          </div>
        </div>
      )}

      <div ref={scrollAnchorRef} />
    </div>
  );

  const chatComposer = (
    <div className={styles.composer}>
      {pendingAttachments}
      <div
        className={`${styles.composerInner} ${!composerLeadingAction ? styles.composerInnerNoLeadingAction : ''}`.trim()}
      >
        {composerLeadingAction}
        <textarea
          ref={inputRef}
          className={styles.input}
          value={draft}
          onChange={(event) => onDraftChange(event.target.value)}
          placeholder={placeholder}
          aria-label={inputAriaLabel}
          rows={1}
          disabled={isAssistantTyping}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              onSend();
            }
          }}
        />

        <IconButton
          icon={FaArrowUp({ 'aria-hidden': 'true' })}
          aria-label={sendAriaLabel}
          variant="primary"
          size="md"
          disabled={!canSend}
          onClick={onSend}
        />
      </div>
      <div className={styles.hintRow}>{footerHint}</div>
    </div>
  );

  if (inline) {
    return (
      <div className={styles.inlinePanel} aria-label={dialogAriaLabel}>
        {chatHeader}
        {chatErrorBanner}
        {chatMessages}
        {chatComposer}
      </div>
    );
  }

  return (
    <>
      <div className={styles.launcher}>
        <IconButton
          ref={launcherRef}
          icon={FaCommentDots({ 'aria-hidden': 'true' })}
          aria-label={launcherAriaLabel}
          variant="primary"
          size="lg"
          onClick={open}
        />
      </div>

      {isOpen && (
        <div ref={overlayRef} className={styles.overlay} role="dialog" aria-modal="true" aria-label={dialogAriaLabel}>
          {chatHeader}
          {chatErrorBanner}
          {chatMessages}
          {chatComposer}
        </div>
      )}
    </>
  );
};

export default AgentChatShell;
